import { css, html, LitElement, nothing } from 'lit'
import { property, state } from 'lit/decorators.js'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import { safeCustomElement } from '@/utilities/custom-element'
import '@/components/Button'
import '@/components/Icon'
import '@/components/Switch'

export interface ComponentOption {
  value: string
  label: string
}

// One intro-link typeahead match returned by the reports searchLinkEntities API.
interface IntroLinkMatch {
  entity: string
  type: string
  name: string
  href: string
  avatarSrc: string
}

// The editor is always served under /reports/; its router dispatches JSON POSTs to the admin API.
const INTRO_LINK_SEARCH_ENDPOINT = '/reports/'

// Anchors are only re-allowed when the href is a bare Models.com people/models/client URL — the
// exact form socialEntityHref() emits. Keep in sync with the allowlist in twRenderIntroText().
const INTRO_LINK_HREF = /^https:\/\/models\.com\/(?:models|people|client)\/[A-Za-z0-9._-]+$/
// Any bare, safe http(s) URL — no chars that could break out of the href="…" attribute or inject
// markup. Allows arbitrary external links inserted via the editor's generic "Insert link" modal
// (blocks javascript:/data: and attribute/tag injection). Mirrors twIsSafeIntroLinkHref() in CF.
const SAFE_HTTP_HREF = /^https?:\/\/[^\s"'<>]+$/i
function isSafeIntroHref(url: string): boolean {
  return INTRO_LINK_HREF.test(url) || SAFE_HTTP_HREF.test(url)
}

// Render intro copy allowing only a small whitelist. Fully escapes the text first,
// then re-enables the bare <b>/<strong> tags (no attributes) — matching the
// server-side twRenderIntroText() in talentWatchPageFunctions.cfm so the editor
// preview and the public viewer stay identical. No injection surface: a tag with
// attributes never matches the exact escaped form, so it stays escaped.
function sanitizeIntroBold(raw: string): string {
  const escaped = raw.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return escaped.replace(/&lt;(\/?)(b|strong)&gt;/gi, '<$1$2>')
}

// Re-allow ONLY <a> whose href passes INTRO_LINK_HREF. Runs on already-bold-sanitized, HTML-escaped
// text: the opening tag survives as &lt;a href="URL"&gt; (sanitizeIntroBold escapes &<> but not
// quotes/slashes), so we match that exact shape, validate the URL, and re-enable just those. Any
// other scheme/host/attribute stays escaped as plain text. Mirrors twRenderIntroText().
function sanitizeIntroLinks(escaped: string): string {
  return escaped
    .replace(/&lt;a href="([^"&]+)"&gt;/gi, (m, url: string) =>
      isSafeIntroHref(url) ? `<a href="${url}">` : m
    )
    .replace(/&lt;\/a&gt;/gi, '</a>')
}

// Full intro whitelist: bold first, then links (order matters — links run on the bold output).
function sanitizeIntro(raw: string): string {
  return sanitizeIntroLinks(sanitizeIntroBold(raw))
}

// Splice <a href="href">name</a> at the textarea caret, then fire `input` so listeners sync .value.
function insertLinkAtCursor(ta: HTMLTextAreaElement, name: string, href: string): void {
  const start = ta.selectionStart ?? ta.value.length
  const end = ta.selectionEnd ?? ta.value.length
  const snippet = `<a href="${href}">${name}</a>`
  ta.value = `${ta.value.slice(0, start)}${snippet}${ta.value.slice(end)}`
  const caret = start + snippet.length
  ta.selectionStart = caret
  ta.selectionEnd = caret
  ta.focus()
  ta.dispatchEvent(new Event('input', { bubbles: true }))
}

// Wrap the textarea's current selection in <b>…</b> (or insert an empty pair at
// the caret), then restore a sensible selection and fire `input` so listeners sync.
function wrapSelectionInBold(ta: HTMLTextAreaElement): void {
  const start = ta.selectionStart ?? ta.value.length
  const end = ta.selectionEnd ?? ta.value.length
  const selected = ta.value.slice(start, end)
  const before = ta.value.slice(0, start)
  const after = ta.value.slice(end)
  ta.value = `${before}<b>${selected}</b>${after}`
  // Reselect the original text (now inside the tags) so a second click / typing continues naturally.
  ta.selectionStart = start + 3
  ta.selectionEnd = start + 3 + selected.length
  ta.focus()
  ta.dispatchEvent(new Event('input', { bubbles: true }))
}

@safeCustomElement('mdc-edit-section-wrapper')
export class MdcEditSectionWrapper extends LitElement {
  @property({ type: String, attribute: 'section-title' }) sectionTitle = ''
  // Computed "what this section is" label (e.g. "Models viewed by Agency Users"). Shown as a
  // read-only descriptive line under the editable title so the editor knows the section's identity
  // even after renaming it. Empty for sections that own their titles elsewhere (Show/Hashtag).
  @property({ type: String, attribute: 'default-title' }) defaultTitle = ''
  // When set, the title renders as an editable input that emits `section-title-change`. When absent
  // the title is static text (Show Appearances / Hashtag sections edit their titles in their own forms).
  @property({ type: Boolean, attribute: 'title-editable' }) titleEditable = false
  @property({ type: String, attribute: 'section-key' }) sectionKey = ''
  @property({ type: Boolean }) included = false
  @property({ type: Boolean, attribute: 'edit-mode' }) editMode = false
  @property({ type: String, attribute: 'component-type' }) componentType = ''
  @property({ type: Array, attribute: false }) componentOptions: ComponentOption[] = []
  @property({ type: Number }) index = 0
  @property({ type: Number, attribute: 'total-sections' }) totalSections = 0
  @property({ type: Number, attribute: 'record-limit' }) recordLimit = 5
  @property({ type: Array, attribute: false }) recordLimitOptions: number[] = [5, 10, 20]
  @property({ type: String, attribute: 'intro-text' }) introText = ''

  // Intro-link typeahead state (per section). Not attributes — purely local UI state.
  @state() private _linkQuery = ''
  @state() private _linkResults: IntroLinkMatch[] = []
  @state() private _linkOpen = false
  private _linkTimer: number | undefined

  static styles = css`
    :host {
      display: block;
    }

    :host([hidden]) {
      display: none;
    }

    .wrapper {
      position: relative;
      border-radius: var(--radius-md, 0.5rem);
      transition: opacity 0.2s, border-color 0.2s;
    }

    .wrapper--edit {
      box-sizing: border-box;
      border: 1px solid var(--color-functional-gray-200);
      padding: var(--spacing-100, 1rem);
    }

    .toolbar {
      display: flex;
      align-items: center;
      gap: var(--spacing-100, 1rem);
      padding: var(--spacing-075, 0.75rem) 0;
      margin-bottom: var(--spacing-075, 0.75rem);
      border-bottom: 1px solid var(--color-functional-gray-200);
      flex-wrap: wrap;
      transition: border-bottom-color 0.3s ease, margin-bottom 0.3s ease;
    }

    .toolbar--collapsed {
      border-bottom-color: transparent;
      margin-bottom: 0;
    }

    .toolbar__title {
      font-family: var(--typography-font-family-sans);
      font-size: var(--typography-font-size-087-and-half);
      font-weight: 600;
      line-height: 1.3;
      color: var(--color-functional-gray-black);
      flex: 1;
      min-width: 0;
    }

    .toolbar__title-edit {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-025, 0.25rem);
    }

    .toolbar__title-input {
      font-family: var(--typography-font-family-sans);
      font-size: var(--typography-font-size-087-and-half);
      font-weight: 600;
      line-height: 1.3;
      color: var(--color-functional-gray-black);
      padding: var(--spacing-025, 0.25rem) var(--spacing-050, 0.5rem);
      border: 1px solid var(--color-functional-gray-200);
      border-radius: var(--radius-sm, 0.25rem);
      background-color: var(--color-functional-gray-white);
      width: 100%;
      box-sizing: border-box;
    }

    .toolbar__title-input:focus {
      outline: 2px solid var(--color-functional-gray-400);
      outline-offset: 1px;
    }

    .toolbar__title-desc {
      font-family: var(--typography-font-family-sans);
      font-size: var(--typography-font-size-075);
      font-weight: 450;
      color: var(--color-functional-gray-600);
    }

    .toolbar__toggle {
      display: flex;
      align-items: center;
      gap: var(--spacing-050, 0.5rem);
    }

    .toolbar__toggle-label {
      font-family: var(--typography-font-family-sans);
      font-size: var(--typography-font-size-075);
      font-weight: 450;
      color: var(--color-functional-gray-600);
    }

    .toolbar__select {
      font-family: var(--typography-font-family-sans);
      font-size: var(--typography-font-size-075);
      padding: var(--spacing-025, 0.25rem) var(--spacing-050, 0.5rem);
      border: 1px solid var(--color-functional-gray-200);
      border-radius: var(--radius-sm, 0.25rem);
      background-color: var(--color-functional-gray-white);
      color: var(--color-functional-gray-black);
      cursor: pointer;
    }

    .toolbar__select:focus {
      outline: 2px solid var(--color-functional-gray-400);
      outline-offset: 1px;
    }

    .toolbar__intro {
      flex: 1 0 100%;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-025, 0.25rem);
    }

    .toolbar__intro-header {
      display: flex;
      align-items: center;
      gap: var(--spacing-050, 0.5rem);
    }

    .toolbar__intro-label {
      font-family: var(--typography-font-family-sans);
      font-size: var(--typography-font-size-075);
      font-weight: 450;
      color: var(--color-functional-gray-600);
    }

    .toolbar__intro-textarea {
      font-family: var(--typography-font-family-sans);
      font-size: var(--typography-font-size-075);
      line-height: 1.4;
      padding: var(--spacing-050, 0.5rem);
      border: 1px solid var(--color-functional-gray-200);
      border-radius: var(--radius-sm, 0.25rem);
      background-color: var(--color-functional-gray-white);
      color: var(--color-functional-gray-black);
      resize: vertical;
      width: 100%;
      box-sizing: border-box;
    }

    .toolbar__intro-textarea:focus {
      outline: 2px solid var(--color-functional-gray-400);
      outline-offset: 1px;
    }

    .toolbar__intro-linksearch {
      position: relative;
      display: inline-flex;
    }

    .toolbar__intro-linksearch-input {
      font-family: var(--typography-font-family-sans);
      font-size: var(--typography-font-size-075);
      padding: var(--spacing-025, 0.25rem) var(--spacing-050, 0.5rem);
      border: 1px solid var(--color-functional-gray-200);
      border-radius: var(--radius-sm, 0.25rem);
      min-width: 15rem;
    }

    .toolbar__intro-linksearch-results {
      position: absolute;
      z-index: 30;
      top: calc(100% + 2px);
      left: 0;
      min-width: 18rem;
      max-height: 20rem;
      overflow-y: auto;
      background: var(--color-functional-gray-white, #fff);
      border: 1px solid var(--color-functional-gray-200);
      border-radius: var(--radius-sm, 0.25rem);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
    }

    .linksearch-result {
      display: flex;
      align-items: center;
      gap: var(--spacing-050, 0.5rem);
      width: 100%;
      padding: var(--spacing-050, 0.5rem);
      border: 0;
      background: none;
      text-align: left;
      cursor: pointer;
      font: inherit;
    }

    .linksearch-result:hover {
      background: var(--color-functional-gray-100);
    }

    .linksearch-avatar {
      width: 1.75rem;
      height: 1.75rem;
      border-radius: 50%;
      object-fit: cover;
      flex: 0 0 auto;
      background: var(--color-functional-gray-100);
    }

    .linksearch-name {
      flex: 1 1 auto;
    }

    .linksearch-type {
      font-size: var(--typography-font-size-050, 0.72rem);
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: var(--color-functional-gray-600);
      white-space: nowrap;
    }

    .linksearch-noresult {
      padding: var(--spacing-050, 0.5rem);
      font-size: var(--typography-font-size-075);
      color: var(--color-functional-gray-600);
    }

    .section-order {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--spacing-050, 0.5rem);
    }

    .move-buttons {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-025, 0.25rem);
    }

    .move-buttons button {
      padding: var(--spacing-025, 0.25rem);
      color: var(--color-functional-gray-400);
      background: transparent;
      border: none;
      border-radius: var(--radius-sm, 0.25rem);
      cursor: pointer;
      transition: color 0.2s, background-color 0.2s;
    }

    .move-buttons button:hover:not(:disabled) {
      color: var(--color-functional-gray-600);
      background-color: var(--color-functional-gray-100);
    }

    .move-buttons button:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    .move-to {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--spacing-025, 0.25rem);
    }

    .move-to__input {
      width: 3rem;
      text-align: center;
      font-family: var(--typography-font-family-sans);
      font-size: var(--typography-font-size-075);
      padding: var(--spacing-025, 0.25rem);
      border: 1px solid var(--color-functional-gray-200);
      border-radius: var(--radius-sm, 0.25rem);
      background-color: var(--color-functional-gray-white);
      color: var(--color-functional-gray-black);
      box-sizing: border-box;
    }

    .move-to__input:focus {
      outline: 2px solid var(--color-functional-gray-400);
      outline-offset: 1px;
    }

    .move-to__btn {
      font-family: var(--typography-font-family-sans);
      font-size: var(--typography-font-size-062-and-half);
      font-weight: 500;
      white-space: nowrap;
      padding: var(--spacing-025, 0.25rem) var(--spacing-050, 0.5rem);
      color: var(--color-functional-gray-600);
      background-color: var(--color-functional-gray-100);
      border: 1px solid var(--color-functional-gray-200);
      border-radius: var(--radius-sm, 0.25rem);
      cursor: pointer;
      transition: color 0.2s, background-color 0.2s;
    }

    .move-to__btn:hover {
      color: var(--color-functional-gray-black);
      background-color: var(--color-functional-gray-200);
    }

    mdc-icon {
      width: 1rem;
      height: 1rem;
    }

    .section-intro {
      font-family: var(--typography-font-family-sans);
      font-size: var(--typography-font-size-100, 16px);
      font-weight: 400;
      line-height: 1.5;
      color: var(--color-functional-gray-600, #555);
      max-width: 720px;
      margin: 0 0 var(--spacing-100, 1rem);
    }

    /* Inline links inserted into intro copy: bold, no underline, same size as the surrounding
       text (inherit), hover to a lighter gray. Mirrors .tw-section-intro a in viewReport.css. */
    .section-intro a,
    .section-intro a:visited {
      color: var(--color-functional-gray-black, #111);
      font-weight: 700;
      text-decoration: none;
    }
    .section-intro a:hover {
      text-decoration: underline;
    }

    .content {
      display: grid;
      grid-template-rows: 1fr;
      transition: grid-template-rows 0.3s ease;
    }

    .content--collapsed {
      grid-template-rows: 0fr;
    }

    .content__inner {
      min-height: 0;
      overflow: hidden;
    }

    .content__inner--hidden {
      content-visibility: hidden;
    }
  `

  private _handleToggle(e: CustomEvent<{ checked: boolean }>) {
    const included = e.detail.checked
    this.included = included
    this.dispatchEvent(
      new CustomEvent('section-toggle', {
        detail: { sectionKey: this.sectionKey, included },
        bubbles: true,
        composed: true,
      })
    )
  }

  private _handleComponentChange(e: Event) {
    const select = e.target as HTMLSelectElement
    const componentType = select.value
    this.componentType = componentType
    this.dispatchEvent(
      new CustomEvent('component-change', {
        detail: { sectionKey: this.sectionKey, componentType },
        bubbles: true,
        composed: true,
      })
    )
  }

  private _handleMoveUp() {
    this.dispatchEvent(
      new CustomEvent('section-move-up', {
        detail: { sectionKey: this.sectionKey, index: this.index },
        bubbles: true,
        composed: true,
      })
    )
  }

  private _handleMoveDown() {
    this.dispatchEvent(
      new CustomEvent('section-move-down', {
        detail: { sectionKey: this.sectionKey, index: this.index },
        bubbles: true,
        composed: true,
      })
    )
  }

  // Jump the section to an explicit 1-based position typed into the input. Clamped to
  // 1..totalSections; a no-op if it resolves to the current spot. The editor JS moves the
  // DOM node and re-persists the order (which resets index/totalSections, refreshing the input).
  private _handleMoveTo() {
    const input = this.shadowRoot?.querySelector<HTMLInputElement>('.move-to__input')
    if (!input) return
    const requested = parseInt(input.value, 10)
    if (Number.isNaN(requested)) {
      input.value = String(this.index + 1)
      return
    }
    const clamped = Math.min(Math.max(requested, 1), this.totalSections)
    const targetIndex = clamped - 1
    if (targetIndex === this.index) {
      input.value = String(this.index + 1)
      return
    }
    this.dispatchEvent(
      new CustomEvent('section-move-to', {
        detail: { sectionKey: this.sectionKey, index: this.index, targetIndex },
        bubbles: true,
        composed: true,
      })
    )
  }

  private _handleMoveToKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault()
      this._handleMoveTo()
    }
  }

  private _handleRecordLimitChange(e: Event) {
    const select = e.target as HTMLSelectElement
    const recordLimit = parseInt(select.value, 10)
    this.recordLimit = recordLimit
    this.dispatchEvent(
      new CustomEvent('record-limit-change', {
        detail: { sectionKey: this.sectionKey, recordLimit },
        bubbles: true,
        composed: true,
      })
    )
  }

  private _handleTitleInput(e: Event) {
    this.sectionTitle = (e.target as HTMLInputElement).value
  }

  // Persist on commit (blur / Enter), not per keystroke — mirrors the intro-text Save behaviour
  // without needing a separate button for a single-line field.
  private _handleTitleSave() {
    this.dispatchEvent(
      new CustomEvent('section-title-change', {
        detail: { sectionKey: this.sectionKey, title: this.sectionTitle.trim() },
        bubbles: true,
        composed: true,
      })
    )
  }

  private _handleTitleKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault()
      ;(e.target as HTMLInputElement).blur()
    }
  }

  private _handleIntroInput(e: Event) {
    this.introText = (e.target as HTMLTextAreaElement).value
  }

  private _handleIntroBold() {
    const ta = this.shadowRoot?.querySelector<HTMLTextAreaElement>('.toolbar__intro-textarea')
    if (!ta) return
    wrapSelectionInBold(ta)
    // wrapSelectionInBold fires `input`, which _handleIntroInput handles — but that
    // listener only runs for user events on the element; call through to be explicit.
    this.introText = ta.value
  }

  // Generic "Insert link" for arbitrary (non-database) URLs. Distinct from the entity typeahead
  // below, which searches models/people/clients. The modal lives in the editor page (light DOM), so
  // the component captures its shadow textarea's current selection and hands the page an `insert`
  // callback that splices the built anchor back in. Keep in sync with the report-intro + spotlight
  // blurb link buttons in talentWatchReportEditor.js.
  private _handleIntroLink() {
    const ta = this.shadowRoot?.querySelector<HTMLTextAreaElement>('.toolbar__intro-textarea')
    if (!ta) return
    const start = ta.selectionStart ?? ta.value.length
    const end = ta.selectionEnd ?? ta.value.length
    const selected = ta.value.slice(start, end)
    this.dispatchEvent(
      new CustomEvent('tw:open-link-modal', {
        detail: {
          selectedText: selected,
          insert: (url: string, text: string) => {
            const snippet = `<a href="${url}">${text}</a>`
            ta.value = `${ta.value.slice(0, start)}${snippet}${ta.value.slice(end)}`
            const caret = start + snippet.length
            ta.selectionStart = caret
            ta.selectionEnd = caret
            ta.focus()
            ta.dispatchEvent(new Event('input', { bubbles: true }))
            this.introText = ta.value
          },
        },
        bubbles: true,
        composed: true,
      })
    )
  }

  private _handleIntroSave() {
    this.dispatchEvent(
      new CustomEvent('section-intro-change', {
        detail: { sectionKey: this.sectionKey, introText: this.introText },
        bubbles: true,
        composed: true,
      })
    )
  }

  // Intro-link typeahead: strict full-name search (models + crew + clients), 100ms debounce,
  // min 2 chars. Mirrors the report-intro version in talentWatchReportEditor.js.
  private _handleLinkInput(e: Event) {
    const q = (e.target as HTMLInputElement).value
    this._linkQuery = q
    if (this._linkTimer) clearTimeout(this._linkTimer)
    const query = q.trim()
    if (query.length < 2) {
      this._linkOpen = false
      this._linkResults = []
      return
    }
    this._linkTimer = window.setTimeout(() => this._runLinkSearch(query), 100)
  }

  private async _runLinkSearch(query: string) {
    try {
      const res = await fetch(INTRO_LINK_SEARCH_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'searchLinkEntities', nameQuery: query }),
      })
      const data = await res.json()
      if (data?.success) {
        this._linkResults = Array.isArray(data.matches) ? data.matches : []
        this._linkOpen = true
      }
    } catch {
      this._linkResults = []
      this._linkOpen = false
    }
  }

  // mousedown (not click) + preventDefault so the pick registers before the input's blur closes
  // the dropdown. Insertion targets the textarea's last caret position, which it retains on blur.
  private _handleLinkPick(e: Event, m: IntroLinkMatch) {
    e.preventDefault()
    const ta = this.shadowRoot?.querySelector<HTMLTextAreaElement>('.toolbar__intro-textarea')
    if (ta) {
      insertLinkAtCursor(ta, m.name, m.href)
      this.introText = ta.value
    }
    this._linkOpen = false
    this._linkResults = []
    this._linkQuery = ''
  }

  private _handleLinkBlur() {
    // Delay so a result's mousedown fires before the dropdown closes.
    window.setTimeout(() => {
      this._linkOpen = false
    }, 150)
  }

  private _renderToolbar() {
    const canMoveUp = this.index > 0
    const canMoveDown = this.index < this.totalSections - 1
    const hasOptions = this.componentOptions.length > 1

    return html`
      <div class="toolbar ${!this.included ? 'toolbar--collapsed' : ''}">
        <div class="section-order">
          <div class="move-buttons">
            <button
              type="button"
              title="Move up"
              ?disabled=${!canMoveUp}
              @click=${this._handleMoveUp}
            >
              <mdc-icon name="chevron-up"></mdc-icon>
            </button>
            <button
              type="button"
              title="Move down"
              ?disabled=${!canMoveDown}
              @click=${this._handleMoveDown}
            >
              <mdc-icon name="chevron-down"></mdc-icon>
            </button>
          </div>
          <div class="move-to">
            <input
              class="move-to__input"
              type="number"
              min="1"
              max=${this.totalSections}
              .value=${String(this.index + 1)}
              aria-label="Section position"
              title="Current position"
              @keydown=${this._handleMoveToKeydown}
            />
            <button
              type="button"
              class="move-to__btn"
              title="Move section to this position"
              @click=${this._handleMoveTo}
            >
              Move
            </button>
          </div>
        </div>

        ${
          this.titleEditable
            ? html`
              <div class="toolbar__title-edit">
                <input
                  class="toolbar__title-input"
                  type="text"
                  .value=${this.sectionTitle}
                  placeholder=${this.defaultTitle}
                  aria-label="Section title"
                  @input=${this._handleTitleInput}
                  @change=${this._handleTitleSave}
                  @keydown=${this._handleTitleKeydown}
                />
                ${
                  this.defaultTitle
                    ? html`<span class="toolbar__title-desc">${this.defaultTitle}</span>`
                    : nothing
                }
              </div>
            `
            : html`<span class="toolbar__title">${this.sectionTitle}</span>`
        }

        <div class="toolbar__toggle">
          <span class="toolbar__toggle-label">Include</span>
          <mdc-switch
            size="sm"
            ?checked=${this.included}
            @change=${this._handleToggle}
          ></mdc-switch>
        </div>

        ${
          hasOptions
            ? html`
              <select
                class="toolbar__select"
                @change=${this._handleComponentChange}
              >
                ${this.componentOptions.map(
                  (opt) => html`
                    <option value=${opt.value} ?selected=${opt.value === this.componentType}>
                      ${opt.label}
                    </option>
                  `
                )}
              </select>
            `
            : nothing
        }

        <select
          class="toolbar__select"
          @change=${this._handleRecordLimitChange}
        >
          ${this.recordLimitOptions.map(
            (opt) => html`
              <option value=${opt} ?selected=${opt === this.recordLimit}>
                ${opt} records
              </option>
            `
          )}
        </select>

        <div class="toolbar__intro">
          <div class="toolbar__intro-header">
            <span class="toolbar__intro-label">Section intro text</span>
            <mdc-button
              variant="secondary"
              size="sm"
              @click=${this._handleIntroBold}
            >
              Bold
            </mdc-button>
            <mdc-button
              variant="secondary"
              size="sm"
              @click=${this._handleIntroLink}
            >
              Link
            </mdc-button>
            <mdc-button
              variant="secondary"
              size="sm"
              @click=${this._handleIntroSave}
            >
              Save
            </mdc-button>
            <div class="toolbar__intro-linksearch">
              <input
                class="toolbar__intro-linksearch-input"
                type="text"
                placeholder="Insert link — search a model, person or brand…"
                autocomplete="off"
                aria-label="Search a model, person or brand to insert as a link at the cursor"
                .value=${this._linkQuery}
                @input=${this._handleLinkInput}
                @blur=${this._handleLinkBlur}
              />
              ${
                this._linkOpen
                  ? html`
                    <div class="toolbar__intro-linksearch-results" role="listbox">
                      ${
                        this._linkResults.length === 0
                          ? html`<div class="linksearch-noresult">
                            No matching model, person or brand.
                          </div>`
                          : this._linkResults.map(
                              (m) => html`
                              <button
                                type="button"
                                class="linksearch-result"
                                role="option"
                                @mousedown=${(e: Event) => this._handleLinkPick(e, m)}
                              >
                                <img
                                  class="linksearch-avatar"
                                  src=${m.avatarSrc}
                                  alt=""
                                  loading="lazy"
                                />
                                <span class="linksearch-name">${m.name}</span>
                                <span class="linksearch-type">${m.type}</span>
                              </button>
                            `
                            )
                      }
                    </div>
                  `
                  : nothing
              }
            </div>
          </div>
          <textarea
            class="toolbar__intro-textarea"
            rows="8"
            placeholder="Optional intro copy shown under this section heading"
            .value=${this.introText}
            @input=${this._handleIntroInput}
          ></textarea>
        </div>
      </div>
    `
  }

  private _renderIntro() {
    // Preview / read-only: show the intro copy only when it has text.
    // In edit mode the intro is edited via the toolbar textarea instead.
    const text = this.introText?.trim()
    if (this.editMode || !text) return nothing
    return html`<p class="section-intro">${unsafeHTML(sanitizeIntro(text))}</p>`
  }

  render() {
    // Not in edit mode and not included: hide entirely
    if (!this.editMode && !this.included) {
      this.hidden = true
      return nothing
    }
    this.hidden = false

    const wrapperClasses = ['wrapper', this.editMode ? 'wrapper--edit' : '']
      .filter(Boolean)
      .join(' ')

    return html`
      <div class=${wrapperClasses}>
        ${this.editMode ? this._renderToolbar() : nothing}
        ${this._renderIntro()}
        <div class="content ${this.editMode && !this.included ? 'content--collapsed' : ''}">
          <div class="content__inner ${this.editMode && !this.included ? 'content__inner--hidden' : ''}">
            <slot></slot>
          </div>
        </div>
      </div>
    `
  }
}
